/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      // The notification describes the last item added through a product form.
      // Generic cart edits have no such item; close it instead of refreshing
      // its item-specific fragments (which can no longer exist after removal).
      if (tag === 'cart-notification' && entry.id !== 'cart-icon-bubble') continue;
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// Cart JSON and section HTML use separate GET endpoints. /cart.js ignores
// sections=; the Section Rendering API lives on /cart?sections=.
async function refreshDawnCartUI() {
  document.querySelector('cart-notification')?.close();
  const sections = collectCartSections();
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const fetchJson = async (url) => {
    // Accept: application/json makes Shopify's /cart route return cart JSON
    // instead of section HTML, even when sections= is present.
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
    return response.json();
  };
  const sectionIds = [...sections.keys()];
  // Shopify accepts at most five sections per request.
  const sectionRequests = [];
  for (let index = 0; index < sectionIds.length; index += 5) {
    sectionRequests.push(fetchJson(`${cartUrl}?sections=${sectionIds.slice(index, index + 5).join(',')}`));
  }
  const [cartData, ...sectionResponses] = await Promise.all([
    fetchJson(`${cartUrl}.js`),
    ...sectionRequests,
  ]);
  if (!Array.isArray(cartData?.items)) throw new Error('Cart refresh returned invalid cart data');
  const renderedSections = Object.assign({}, ...sectionResponses);

  for (const [id, { mount, extractSelector }] of sections) {
    const html = renderedSections[id];
    if (!html) throw new Error(`Cart section ${id} could not be rendered`);
    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(extractSelector);
    if (!source) throw new Error(`Cart section ${id} is missing its content`);
    mount.replaceChildren(...source.childNodes);
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …);
  // quick-add-bulk.js and price-per-item.js read it directly.
  await publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData,
  });

  // Dawn's pubsub refresh replaces inner HTML, leaving these outer empty-state
  // classes and the cart-page checkout button behind. Keep them in sync when
  // adding the first item or removing the last item through Standard Actions.
  const isEmpty = cartData.item_count === 0;
  document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer')
    .forEach((element) => element.classList.toggle('is-empty', isEmpty));
  document.querySelectorAll('#checkout, #CartDrawer-Checkout')
    .forEach((button) => { button.disabled = isEmpty; });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
